<script setup>
import { router } from '@inertiajs/vue3';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, phpVersions, cms } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    phpVersions: {
        type: Array,
        required: true,
    },
    cms: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const form = reactive({
    cms:
        tenant && tenant.cms
            ? cms.find((cms) => cms.value === tenant.cms)
            : null,
    cmsVersion: tenant ? tenant.cmsVersion : '',
    cmsVersionMaintainedUntil: tenant ? tenant.cmsVersionMaintainedUntil : '',
    openmage: tenant ? tenant.openmage : false,
    phpVersion:
        tenant && tenant.phpVersion
            ? phpVersions.find(
                  (phpVersion) => phpVersion.value === tenant.phpVersion,
              )
            : null,
    cspEnabled: tenant ? tenant.cspEnabled : false,
    twoFactorAuthenticationEnabled: tenant
        ? tenant.twoFactorAuthenticationEnabled
        : false,
    securityNotes: tenant ? tenant.securityNotes : '',
    cves: initializeCves(tenant),
});

const validationSchema = object({
    cms: object().nullable(),
    cmsVersion: string().nullable(),
    cmsVersionMaintainedUntil: string().nullable(),
    openmage: boolean().nullable(),
    phpVersion: object().nullable(),
    cspEnabled: boolean().nullable(),
    securityNotes: string().nullable(),
    twoFactorAuthenticationEnabled: boolean().nullable(),
});

const isSubmitting = ref(false);

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.dev.postWebsite', {
                tenant: tenant.id,
            }),
            {
                cms: form.cms ? form.cms.value : null,
                cmsVersion: form.cmsVersion,
                cmsVersionMaintainedUntil: form.cmsVersionMaintainedUntil,
                openmage: form.openmage ? form.openmage : false,
                phpVersion: form.phpVersion ? form.phpVersion.value : null,
                cspEnabled: form.cspEnabled ? form.cspEnabled : false,
                twoFactorAuthenticationEnabled:
                    form.twoFactorAuthenticationEnabled
                        ? form.twoFactorAuthenticationEnabled
                        : false,
                securityNotes: form.securityNotes,
                cves: form.cves,
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route('front.projectManagement.dev.website', {
                    tenant: tenant.id,
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.dev.website', {
                    tenant: tenant.id,
                }),
            );
        });
}

function initializeCves(tenant) {
    const cves = {};

    if (tenant && tenant.cves) {
        tenant.cves.forEach((cve) => {
            cves[cve.id] = cve.isApplied;
        });
    }

    return cves;
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                cves: {
                    title: 'CVEs',
                    contentSlotName: 'cves',
                    forceMount: true,
                },
            }"
            :unmount-on-hide="false"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid>
                            <ListboxField
                                v-model="form.cms"
                                field-id="cms"
                                field-name="cms"
                                label="CMS/Framework"
                                :options="cms"
                                has-empty-option
                            >
                                <template #buttonContent>
                                    {{ form.cms.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <InputField
                                v-if="
                                    form.cms &&
                                    [
                                        'magento1',
                                        'magento2',
                                        'prestashop',
                                        'wordpress',
                                        'laravel',
                                        'nextjs',
                                    ].includes(form.cms.value)
                                "
                                v-model="form.cmsVersion"
                                label="Version du CMS/Framework"
                                field-name="cmsVersion"
                                field-id="cmsVersion"
                                type="text"
                            />

                            <InputField
                                v-if="
                                    form.cms &&
                                    [
                                        'magento1',
                                        'magento2',
                                        'prestashop',
                                        'wordpress',
                                        'laravel',
                                        'nextjs',
                                    ].includes(form.cms.value)
                                "
                                v-model="form.cmsVersionMaintainedUntil"
                                label="Version du CMS/Framework maintenue jusqu'à"
                                field-name="cmsVersionMaintainedUntil"
                                field-id="cmsVersionMaintainedUntil"
                                type="date"
                            />

                            <SwitchField
                                v-if="form.cms && form.cms.value === 'magento1'"
                                field-name="openmage"
                                field-id="openmage"
                                v-model="form.openmage"
                                label="OpenMage"
                            />

                            <ListboxField
                                v-if="
                                    form.cms &&
                                    [
                                        'magento1',
                                        'magento2',
                                        'prestashop',
                                        'wordpress',
                                        'laravel',
                                        'static',
                                    ].includes(form.cms.value)
                                "
                                v-model="form.phpVersion"
                                field-id="phpVersion"
                                field-name="phpVersion"
                                label="Version de PHP"
                                :options="phpVersions"
                                has-empty-option
                            >
                                <template #buttonContent>
                                    {{ form.phpVersion.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>

                            <SwitchField
                                v-if="
                                    form.cms &&
                                    ['magento1', 'magento2'].includes(
                                        form.cms.value,
                                    )
                                "
                                field-name="cspEnabled"
                                field-id="cspEnabled"
                                v-model="form.cspEnabled"
                                label="CSP activés"
                            />

                            <SwitchField
                                v-if="
                                    form.cms &&
                                    ['magento1', 'magento2'].includes(
                                        form.cms.value,
                                    )
                                "
                                field-name="twoFactorAuthenticationEnabled"
                                field-id="twoFactorAuthenticationEnabled"
                                v-model="form.twoFactorAuthenticationEnabled"
                                label="2FA Admin activé"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <EditorField
                            v-model="form.securityNotes"
                            label="Notes de sécurité"
                            field-name="securityNotes"
                            field-id="securityNotes"
                            size="small"
                        />
                    </FormContent>
                </FormColumn>
            </template>

            <template #cves>
                <FormColumn>
                    <FormContent>
                        <SwitchField
                            v-for="(cve, index) in tenant.cves"
                            :key="index"
                            :label="cve.nvdCveId + ' (' + cve.severity + ') '"
                            :field-name="'cve[' + cve.id + ']'"
                            :field-id="'cve[' + cve.id + ']'"
                            v-model="form.cves[cve.id]"
                            :legend="cve.url"
                        />
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
